import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Library, Loader2, Pencil, Plus, Search } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { getCostCategories, getCostTrades, getUnits } from "@shared/schema";

type Market = "uk" | "au" | "ca" | "us";

type DefaultTask = {
  id: string;
  market: string | null;
  category: string;
  trade: string;
  name: string;
  description: string | null;
  unit: string;
  rateLow: string;
  rateTypical: string;
  rateHigh: string;
  labourPct: string | null;
  materialPct: string | null;
  plantPct: string | null;
  keywords: string | null;
  notesIncluded: string | null;
  notesExcluded: string | null;
  canonicalKey: string | null;
  isActive: boolean | null;
  sortOrder: number | null;
  updatedAt: string | null;
  isPriceLocked: boolean | null;
  isAdminEdited: boolean | null;
};

type ListResponse = { tasks: DefaultTask[]; total: number };

type FormState = {
  name: string;
  description: string;
  category: string;
  trade: string;
  unit: string;
  rateLow: string;
  rateTypical: string;
  rateHigh: string;
  labourPct: string;
  materialPct: string;
  plantPct: string;
  keywords: string;
  isActive: boolean;
  isPriceLocked: boolean;
};

const EMPTY_FORM: FormState = {
  name: "",
  description: "",
  category: "",
  trade: "",
  unit: "",
  rateLow: "",
  rateTypical: "",
  rateHigh: "",
  labourPct: "",
  materialPct: "",
  plantPct: "",
  keywords: "",
  isActive: true,
  isPriceLocked: false,
};

const MARKET_LABELS: Record<Market, string> = { uk: "UK", us: "US", ca: "Canada", au: "Australia" };
const CURRENCY: Record<Market, string> = { uk: "£", us: "$", ca: "$", au: "$" };

function isPositiveNumber(v: string): boolean {
  const n = parseFloat(v);
  return !isNaN(n) && n > 0;
}

function isValidPct(v: string): boolean {
  if (v.trim() === "") return true; // blank = fall back to category default
  const n = parseFloat(v);
  return !isNaN(n) && n >= 0 && n <= 1;
}

function taskToForm(task: DefaultTask): FormState {
  return {
    name: task.name,
    description: task.description ?? "",
    category: task.category,
    trade: task.trade,
    unit: task.unit,
    rateLow: task.rateLow,
    rateTypical: task.rateTypical,
    rateHigh: task.rateHigh,
    labourPct: task.labourPct ?? "",
    materialPct: task.materialPct ?? "",
    plantPct: task.plantPct ?? "",
    keywords: task.keywords ?? "",
    isActive: task.isActive !== false,
    isPriceLocked: task.isPriceLocked === true,
  };
}

function formToPayload(form: FormState) {
  return {
    name: form.name.trim(),
    description: form.description.trim() || null,
    category: form.category,
    trade: form.trade,
    unit: form.unit,
    rateLow: form.rateLow,
    rateTypical: form.rateTypical,
    rateHigh: form.rateHigh,
    labourPct: form.labourPct.trim() === "" ? null : form.labourPct,
    materialPct: form.materialPct.trim() === "" ? null : form.materialPct,
    plantPct: form.plantPct.trim() === "" ? null : form.plantPct,
    keywords: form.keywords.trim() || null,
    isActive: form.isActive,
    isPriceLocked: form.isPriceLocked,
  };
}

function TaskFormFields({
  form,
  setForm,
  market,
}: {
  form: FormState;
  setForm: (updater: (prev: FormState) => FormState) => void;
  market: Market;
}) {
  const categories = getCostCategories(market);
  const trades = getCostTrades(market);
  const units = Object.entries(getUnits(market));
  const tradesForCategory = form.category ? (trades[form.category]?.trades ?? []) : [];
  const cur = CURRENCY[market];

  function update(field: keyof FormState, value: string | boolean) {
    setForm((prev) => ({
      ...prev,
      [field]: value,
      ...(field === "category" ? { trade: "" } : {}),
    }));
  }

  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <Label className="text-xs">Name</Label>
        <Input
          value={form.name}
          onChange={(e) => update("name", e.target.value)}
          placeholder="e.g. Install steel beam"
          className="h-8 text-sm"
          data-testid="input-master-name"
        />
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Description (optional)</Label>
        <Textarea
          value={form.description}
          onChange={(e) => update("description", e.target.value)}
          className="min-h-[54px] text-sm"
          data-testid="input-master-description"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs">Category</Label>
          <Select value={form.category} onValueChange={(v) => update("category", v)}>
            <SelectTrigger className="h-8 text-xs" data-testid="select-master-category">
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent>
              {categories.map((cat) => (
                <SelectItem key={cat.value} value={cat.value} className="text-xs">
                  {cat.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Trade</Label>
          <Select
            value={form.trade}
            onValueChange={(v) => update("trade", v)}
            disabled={tradesForCategory.length === 0}
          >
            <SelectTrigger className="h-8 text-xs" data-testid="select-master-trade">
              <SelectValue placeholder={tradesForCategory.length === 0 ? "Pick category first" : "Select trade"} />
            </SelectTrigger>
            <SelectContent>
              {tradesForCategory.map((t) => (
                <SelectItem key={t.value} value={t.value} className="text-xs">
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Unit</Label>
        <Select value={form.unit} onValueChange={(v) => update("unit", v)}>
          <SelectTrigger className="h-8 text-xs" data-testid="select-master-unit">
            <SelectValue placeholder="Select unit" />
          </SelectTrigger>
          <SelectContent>
            {units.map(([value, label]) => (
              <SelectItem key={value} value={value} className="text-xs">
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {([
          ["rateLow", `Rate Low (${cur})`],
          ["rateTypical", `Rate Typical (${cur})`],
          ["rateHigh", `Rate High (${cur})`],
        ] as const).map(([field, label]) => (
          <div key={field} className="space-y-1">
            <Label className="text-xs">{label}</Label>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={form[field]}
              onChange={(e) => update(field, e.target.value)}
              className="h-8 text-sm"
              placeholder="0.00"
              data-testid={`input-master-${field}`}
            />
          </div>
        ))}
      </div>
      <div>
        <p className="text-xs text-muted-foreground mb-1">
          Labour / material / plant split as fractions of the rate (e.g. 0.55 / 0.40 / 0.05). Leave blank to use the category default.
        </p>
        <div className="grid grid-cols-3 gap-2">
          {([
            ["labourPct", "Labour"],
            ["materialPct", "Material"],
            ["plantPct", "Plant"],
          ] as const).map(([field, label]) => (
            <div key={field} className="space-y-1">
              <Label className="text-xs">{label}</Label>
              <Input
                type="number"
                min="0"
                max="1"
                step="0.01"
                value={form[field]}
                onChange={(e) => update(field, e.target.value)}
                className="h-8 text-sm"
                placeholder="—"
                data-testid={`input-master-${field}`}
              />
            </div>
          ))}
        </div>
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Keywords (comma-separated, used for AI matching)</Label>
        <Input
          value={form.keywords}
          onChange={(e) => update("keywords", e.target.value)}
          placeholder="e.g. steel, beam, rsj"
          className="h-8 text-sm"
          data-testid="input-master-keywords"
        />
      </div>
      <div className="flex items-center gap-2 pt-1">
        <Switch
          checked={form.isActive}
          onCheckedChange={(v) => update("isActive", v)}
          data-testid="switch-master-active"
        />
        <Label className="text-sm">Active (visible in every user's library)</Label>
      </div>
      <div className="rounded-md border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30 p-3 space-y-1.5">
        <div className="flex items-center gap-2">
          <Switch
            checked={form.isPriceLocked}
            onCheckedChange={(v) => update("isPriceLocked", v)}
            data-testid="switch-master-price-locked"
          />
          <Label className="text-sm font-medium">Lock price — AI must use this rate exactly</Label>
        </div>
        <p className="text-xs text-muted-foreground pl-9">
          When on, the AI pipeline applies the Typical rate verbatim. Rate floors, outlier
          caps, and AI flexibility are all bypassed. A user's personal override still wins.
        </p>
      </div>
    </div>
  );
}

function formValid(form: FormState): boolean {
  return (
    form.name.trim().length > 0 &&
    form.category.length > 0 &&
    form.trade.length > 0 &&
    form.unit.length > 0 &&
    isPositiveNumber(form.rateLow) &&
    isPositiveNumber(form.rateTypical) &&
    isPositiveNumber(form.rateHigh) &&
    isValidPct(form.labourPct) &&
    isValidPct(form.materialPct) &&
    isValidPct(form.plantPct)
  );
}

export default function AdminCostLibraryPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [market, setMarket] = useState<Market>("uk");
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [editTarget, setEditTarget] = useState<DefaultTask | null>(null);
  const [editForm, setEditForm] = useState<FormState>(EMPTY_FORM);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState<FormState>(EMPTY_FORM);

  const listKey = `/api/admin/cost-library/defaults?market=${market}`;
  const { data, isLoading } = useQuery<ListResponse>({ queryKey: [listKey] });

  const categories = getCostCategories(market);
  const categoryLabel = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of categories) map.set(c.value, c.label);
    return map;
  }, [categories]);
  const tradeLabel = useMemo(() => {
    const map = new Map<string, string>();
    for (const group of Object.values(getCostTrades(market))) {
      for (const t of group.trades) map.set(t.value, t.label);
    }
    return map;
  }, [market]);
  const cur = CURRENCY[market];

  const tasks = data?.tasks ?? [];
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return tasks.filter((t) => {
      if (categoryFilter !== "all" && t.category !== categoryFilter) return false;
      if (!q) return true;
      return (
        t.name.toLowerCase().includes(q) ||
        (t.keywords ?? "").toLowerCase().includes(q) ||
        (categoryLabel.get(t.category) ?? t.category).toLowerCase().includes(q) ||
        (tradeLabel.get(t.trade) ?? t.trade).toLowerCase().includes(q)
      );
    });
  }, [tasks, search, categoryFilter, categoryLabel, tradeLabel]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: [listKey] });
    queryClient.invalidateQueries({ queryKey: ["/api/cost-library"] });
  };

  const updateMutation = useMutation({
    mutationFn: async () => {
      if (!editTarget) return;
      const res = await apiRequest("PATCH", `/api/admin/cost-library/default/${editTarget.id}`, formToPayload(editForm));
      return res.json();
    },
    onSuccess: () => {
      invalidate();
      toast({ title: "Master item updated", description: "The change is live for all users without a personal override on this item." });
      setEditTarget(null);
    },
    onError: (err) => {
      toast({ title: "Update failed", description: err instanceof Error ? err.message : "Please try again.", variant: "destructive" });
    },
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/cost-library/default", {
        ...formToPayload(addForm),
        market,
      });
      return res.json();
    },
    onSuccess: () => {
      invalidate();
      toast({ title: "Master item added", description: `Added to the ${MARKET_LABELS[market]} default library for all users.` });
      setAddOpen(false);
      setAddForm(EMPTY_FORM);
    },
    onError: (err) => {
      toast({ title: "Add failed", description: err instanceof Error ? err.message : "Please try again.", variant: "destructive" });
    },
  });

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-lg sm:text-2xl font-bold flex items-center gap-2">
            <Library className="h-5 w-5" /> Master Cost Library
          </h1>
          <p className="text-muted-foreground text-sm max-w-2xl">
            The default pricing database every user starts from. Edits go live immediately for all users —
            except items a user has personally overridden (their price wins). Rate changes are recorded for audit.
          </p>
        </div>
        <Button
          onClick={() => { setAddForm(EMPTY_FORM); setAddOpen(true); }}
          className="gap-1.5"
          data-testid="button-master-add"
        >
          <Plus className="h-4 w-4" /> Add item
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Default items — {MARKET_LABELS[market]}</CardTitle>
          <CardDescription>{isLoading ? "Loading…" : `${filtered.length} of ${tasks.length} items`}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <Select value={market} onValueChange={(v) => { setMarket(v as Market); setCategoryFilter("all"); }}>
              <SelectTrigger className="w-36 h-8 text-xs" data-testid="select-master-market">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(MARKET_LABELS) as Market[]).map((m) => (
                  <SelectItem key={m} value={m} className="text-xs">{MARKET_LABELS[m]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-52 h-8 text-xs" data-testid="select-master-category-filter">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="text-xs">All categories</SelectItem>
                {categories.map((c) => (
                  <SelectItem key={c.value} value={c.value} className="text-xs">{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="relative flex-1 min-w-[180px]">
              <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, keywords, category…"
                className="h-8 pl-8 text-sm"
                data-testid="input-master-search"
              />
            </div>
          </div>

          {isLoading ? (
            <p className="text-sm text-muted-foreground py-8 text-center">Loading…</p>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">No items match this filter</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead className="hidden md:table-cell">Category / Trade</TableHead>
                    <TableHead>Unit</TableHead>
                    <TableHead className="text-right">Low</TableHead>
                    <TableHead className="text-right">Typical</TableHead>
                    <TableHead className="text-right">High</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((t) => (
                    <TableRow key={t.id} className={t.isActive === false ? "opacity-60" : undefined}>
                      <TableCell className="max-w-[260px]">
                        <p className="font-medium text-sm truncate" title={t.name}>{t.name}</p>
                        {t.keywords && (
                          <p className="text-xs text-muted-foreground truncate" title={t.keywords}>{t.keywords}</p>
                        )}
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-xs text-muted-foreground whitespace-nowrap">
                        {categoryLabel.get(t.category) ?? t.category}
                        <span className="mx-1">·</span>
                        {tradeLabel.get(t.trade) ?? t.trade}
                      </TableCell>
                      <TableCell className="text-xs whitespace-nowrap">{t.unit}</TableCell>
                      <TableCell className="text-right text-xs whitespace-nowrap">{cur}{t.rateLow}</TableCell>
                      <TableCell className="text-right text-sm font-medium whitespace-nowrap">{cur}{t.rateTypical}</TableCell>
                      <TableCell className="text-right text-xs whitespace-nowrap">{cur}{t.rateHigh}</TableCell>
                      <TableCell>
                        {t.isActive === false ? (
                          <Badge className="text-[10px] bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200">
                            Inactive
                          </Badge>
                        ) : t.isPriceLocked ? (
                          <Badge className="text-[10px] bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200">
                            Set
                          </Badge>
                        ) : t.isAdminEdited ? (
                          <Badge className="text-[10px] bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200">
                            Updated
                          </Badge>
                        ) : (
                          <Badge className="text-[10px] bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                            Active
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs gap-1"
                          onClick={() => { setEditTarget(t); setEditForm(taskToForm(t)); }}
                          data-testid={`button-master-edit-${t.id}`}
                        >
                          <Pencil className="h-3.5 w-3.5" /> Edit
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit dialog */}
      <Dialog open={!!editTarget} onOpenChange={(next) => { if (!next) setEditTarget(null); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit master item</DialogTitle>
            <DialogDescription>
              Changes go live for every user who hasn't personally overridden this item. Rate changes are logged.
            </DialogDescription>
          </DialogHeader>
          <TaskFormFields form={editForm} setForm={setEditForm} market={market} />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditTarget(null)} disabled={updateMutation.isPending}>
              Cancel
            </Button>
            <Button
              onClick={() => updateMutation.mutate()}
              disabled={updateMutation.isPending || !formValid(editForm)}
              data-testid="button-master-save-edit"
            >
              {updateMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add master item — {MARKET_LABELS[market]}</DialogTitle>
            <DialogDescription>
              Adds a new default item to the {MARKET_LABELS[market]} master library, visible to all users immediately.
            </DialogDescription>
          </DialogHeader>
          <TaskFormFields form={addForm} setForm={setAddForm} market={market} />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAddOpen(false)} disabled={createMutation.isPending}>
              Cancel
            </Button>
            <Button
              onClick={() => createMutation.mutate()}
              disabled={createMutation.isPending || !formValid(addForm)}
              data-testid="button-master-save-add"
            >
              {createMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Add item
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
